import {
  Mic,
  MicOff,
  SettingsInputComponent,
  VolumeUp,
  VolumeOff,
} from "@mui/icons-material";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Slider,
  Typography,
  Checkbox,
  FormControlLabel,
  Stack,
  alpha,
} from "@mui/material";
import { useContactCenterTheme } from "../../theming/ContactCenterThemeContext";
import { useContactCenterStyles } from "../../theming/useContactCenterStyles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { menuPropsModal } from "../../../../utils";
import { MicPermissionStatus, MicrophonePermissionEmptyState } from "./MicroPhonePermissionsEmptyState";

const AUDIO_SETTINGS_STORAGE_KEY = "vr-audio-settings";

type LocalAudioSettings = {
  inputSensitivity: number;
  outputVolume: number;
  micMuted: boolean;
  speakerMuted: boolean;
  selectedMic: string;
  selectedSpeaker: string;
};

const defaultAudioSettings: LocalAudioSettings = {
  inputSensitivity: 30,
  outputVolume: 50,
  micMuted: false,
  speakerMuted: false,
  selectedMic: "",
  selectedSpeaker: "",
};

const getStoredAudioSettings = (): LocalAudioSettings => {
  try {
    const raw = localStorage.getItem(AUDIO_SETTINGS_STORAGE_KEY);

    if (!raw) return defaultAudioSettings;

    const parsed = JSON.parse(raw);

    return {
      inputSensitivity: parsed.inputSensitivity ?? 30,
      outputVolume: parsed.outputVolume ?? 50,
      micMuted: parsed.micMuted ?? false,
      speakerMuted: parsed.speakerMuted ?? false,
      selectedMic: parsed.selectedMic ?? "",
      selectedSpeaker: parsed.selectedSpeaker ?? "",
    };
  } catch {
    return defaultAudioSettings;
  }
};


const getBrowserMicPermissionState = async () => {
  try {
    if (!navigator.permissions?.query) {
      return "unknown";
    }

    const permission = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    });

    return permission.state; // "granted" | "denied" | "prompt"
  } catch {
    return "unknown";
  }
};


export default function AudioSettings() {
  const styles = useContactCenterStyles();
  const { theme: contactCenterTheme } = useContactCenterTheme();
  const initialSettings = getStoredAudioSettings();

  const [inputSensitivity, setInputSensitivity] = useState(
    initialSettings.inputSensitivity,
  );
  const [outputVolume, setOutputVolume] = useState(
    initialSettings.outputVolume,
  );
  const [micMuted, setMicMuted] = useState(initialSettings.micMuted);
  const [speakerMuted, setSpeakerMuted] = useState(
    initialSettings.speakerMuted,
  );

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMic, setSelectedMic] = useState(initialSettings.selectedMic);
  const [selectedSpeaker, setSelectedSpeaker] = useState(
    initialSettings.selectedSpeaker,
  );
  const [micLevel, setMicLevel] = useState(0);
  const [isTestingMic, setIsTestingMic] = useState(false);
  const [isTestingSpeaker, setIsTestingSpeaker] = useState(false);
  const [micPermissionStatus, setMicPermissionStatus] =
    useState<MicPermissionStatus>("idle");

  const micStreamRef = useRef<MediaStream | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micGainNodeRef = useRef<GainNode | null>(null);
  const speakerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const RED_COLOR = "#d32f2f";
  const ORANGE_MAIN = contactCenterTheme.shell.accent;
  const ORANGE_LIGHT = alpha(contactCenterTheme.shell.accent, 0.32);

  const persistAudioSettings = useCallback(
    (nextPartial: Partial<LocalAudioSettings>) => {
      const nextSettings: LocalAudioSettings = {
        inputSensitivity,
        outputVolume,
        micMuted,
        speakerMuted,
        selectedMic,
        selectedSpeaker,
        ...nextPartial,
      };

      localStorage.setItem(
        AUDIO_SETTINGS_STORAGE_KEY,
        JSON.stringify(nextSettings),
      );
    },
    [
      inputSensitivity,
      outputVolume,
      micMuted,
      speakerMuted,
      selectedMic,
      selectedSpeaker,
    ],
  );

  const stopAnimation = useCallback(() => {
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
  }, []);

  const stopMicTest = useCallback(() => {
    setIsTestingMic(false);
    setMicLevel(0);

    stopAnimation();

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }

    micGainNodeRef.current = null;
  }, [stopAnimation]);

  const stopSpeakerTest = useCallback(() => {
    setIsTestingSpeaker(false);

    if (speakerTimeoutRef.current) {
      clearTimeout(speakerTimeoutRef.current);
      speakerTimeoutRef.current = null;
    }

    stopAnimation();
    setMicLevel(0);
  }, [stopAnimation]);

  useEffect(() => {
    if (micGainNodeRef.current && audioContextRef.current) {
      const volume = micMuted ? 0 : inputSensitivity / 100;

      micGainNodeRef.current.gain.setTargetAtTime(
        volume,
        audioContextRef.current.currentTime,
        0.05,
      );
    }
  }, [inputSensitivity, micMuted]);

  const getDevices = useCallback(async () => {
    try {
      setMicPermissionStatus("checking");

      if (
        typeof window === "undefined" ||
        !navigator.mediaDevices?.getUserMedia ||
        !navigator.mediaDevices?.enumerateDevices ||
        !window.isSecureContext
      ) {
        setMicPermissionStatus("unsupported");
        return;
      }

      const permissionState = await getBrowserMicPermissionState();

      if (permissionState === "denied") {
        setMicPermissionStatus("denied");
        return;
      }

      const permissionStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      permissionStream.getTracks().forEach((track) => track.stop());

      const deviceInfos = await navigator.mediaDevices.enumerateDevices();

      setDevices(deviceInfos);
      setMicPermissionStatus("granted");

      const audioInputs = deviceInfos.filter((d) => d.kind === "audioinput");
      const audioOutputs = deviceInfos.filter((d) => d.kind === "audiooutput");

      if (audioInputs.length > 0 && !selectedMic) {
        const nextMic = audioInputs[0].deviceId;
        setSelectedMic(nextMic);
        persistAudioSettings({ selectedMic: nextMic });
      }

      if (audioOutputs.length > 0 && !selectedSpeaker) {
        const nextSpeaker = audioOutputs[0].deviceId;
        setSelectedSpeaker(nextSpeaker);
        persistAudioSettings({ selectedSpeaker: nextSpeaker });
      }
    } catch (err) {
      console.error("Microphone permission error:", err);

      if (err instanceof DOMException) {
        if (
          err.name === "NotAllowedError" ||
          err.name === "PermissionDeniedError"
        ) {
          setMicPermissionStatus("denied");
          return;
        }

        if (
          err.name === "NotFoundError" ||
          err.name === "DevicesNotFoundError"
        ) {
          setMicPermissionStatus("unsupported");
          return;
        }
      }

      setMicPermissionStatus("denied");
    }
  }, [selectedMic, selectedSpeaker, persistAudioSettings]);

  useEffect(() => {
    void getDevices();

    return () => {
      stopMicTest();
      stopSpeakerTest();

      void audioContextRef.current?.close();
      audioContextRef.current = null;
    };
  }, [getDevices, stopMicTest, stopSpeakerTest]);

  const initAnalyser = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    }

    if (!analyserRef.current) {
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
    }

    return {
      ctx: audioContextRef.current,
      analyser: analyserRef.current,
    };
  };

  const startAnimation = (analyser: AnalyserNode) => {
    stopAnimation();

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateLevel = () => {
      analyser.getByteFrequencyData(dataArray);

      let sum = 0;

      for (let i = 0; i < dataArray.length; i += 1) {
        sum += dataArray[i];
      }

      setMicLevel(
        Math.min(100, Math.round((sum / dataArray.length / 128) * 100)),
      );

      animationFrameIdRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();
  };

  const startMicTest = async () => {
    if (isTestingMic) {
      stopMicTest();
      return;
    }

    stopSpeakerTest();

    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.isSecureContext) {
        setMicPermissionStatus("unsupported");
        return;
      }

      const { ctx, analyser } = initAnalyser();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedMic ? { exact: selectedMic } : undefined,
        },
      });

      micStreamRef.current = stream;
      setIsTestingMic(true);
      setMicPermissionStatus("granted");

      const source = ctx.createMediaStreamSource(stream);
      const gainNode = ctx.createGain();

      micGainNodeRef.current = gainNode;

      gainNode.gain.setValueAtTime(
        micMuted ? 0 : inputSensitivity / 100,
        ctx.currentTime,
      );

      source.connect(analyser);
      source.connect(gainNode);
      gainNode.connect(ctx.destination);

      startAnimation(analyser);
    } catch (err) {
      console.error("Error testing microphone:", err);

      if (err instanceof DOMException && err.name === "NotAllowedError") {
        setMicPermissionStatus("denied");
      }

      setIsTestingMic(false);
    }
  };

  const playTestSound = () => {
    if (isTestingMic) stopMicTest();

    stopSpeakerTest();

    setIsTestingSpeaker(true);

    const { ctx, analyser } = initAnalyser();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    const safeVolume = speakerMuted
      ? 0.0001
      : Math.max(outputVolume / 100, 0.0001);

    osc.type = "sine";
    osc.frequency.setValueAtTime(440, ctx.currentTime);
    gain.gain.setValueAtTime(safeVolume, ctx.currentTime);

    osc.connect(gain);
    gain.connect(analyser);
    gain.connect(ctx.destination);

    osc.start();
    startAnimation(analyser);

    const duration = 2.0;

    gain.gain.exponentialRampToValueAtTime(
      0.0001,
      ctx.currentTime + duration,
    );

    osc.stop(ctx.currentTime + duration);

    speakerTimeoutRef.current = setTimeout(() => {
      stopSpeakerTest();
    }, duration * 1000);
  };

  const microphones = useMemo(
    () => devices.filter((device) => device.kind === "audioinput"),
    [devices],
  );

  const speakers = useMemo(
    () => devices.filter((device) => device.kind === "audiooutput"),
    [devices],
  );

  if (
    micPermissionStatus === "denied" ||
    micPermissionStatus === "unsupported"
  ) {
    return (
      <MicrophonePermissionEmptyState
        status={micPermissionStatus}
        onRetry={getDevices}
        compact
      />
    );
  }

  return (
    <Box sx={{ ...styles.surface, height: "100%", p: 1 }}>
      <Stack spacing={2.5}>
        <Box sx={styles.card}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <SettingsInputComponent sx={{ color: ORANGE_MAIN }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Hardware
            </Typography>
          </Box>

          <Stack spacing={1.5}>
            <Select
              fullWidth
              size="small"
              value={selectedMic}
              onChange={(e) => {
                const value = e.target.value;

                setSelectedMic(value);
                persistAudioSettings({ selectedMic: value });
              }}
              sx={styles.select}
              MenuProps={{ ...menuPropsModal, disablePortal: true }}
              displayEmpty
              renderValue={(val) =>
                microphones.find((mic) => mic.deviceId === val)?.label ||
                (micPermissionStatus === "checking"
                  ? "Checking microphone..."
                  : "Select microphone")
              }
            >
              {microphones.map((mic) => (
                <MenuItem key={mic.deviceId} value={mic.deviceId}>
                  {mic.label || "Microphone"}
                </MenuItem>
              ))}
            </Select>

            <Select
              fullWidth
              size="small"
              value={selectedSpeaker}
              onChange={(e) => {
                const value = e.target.value;

                setSelectedSpeaker(value);
                persistAudioSettings({ selectedSpeaker: value });
              }}
              MenuProps={{ ...menuPropsModal, disablePortal: true }}
              sx={styles.select}
              displayEmpty
              renderValue={(val) =>
                speakers.find((speaker) => speaker.deviceId === val)?.label ||
                "Default output"
              }
            >
              {speakers.map((speaker) => (
                <MenuItem key={speaker.deviceId} value={speaker.deviceId}>
                  {speaker.label || "Speaker"}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Box>

        <Box sx={styles.card}>
          <Box
            display="flex"
            justifyContent="space-around"
            sx={{
              bgcolor: contactCenterTheme.chat.inputBg,
              borderRadius: "12px",
              p: 0.5,
              mb: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={micMuted}
                  onChange={(e) => {
                    const checked = e.target.checked;

                    setMicMuted(checked);
                    persistAudioSettings({ micMuted: checked });
                  }}
                  icon={<Mic sx={{ fontSize: 18, color: ORANGE_MAIN }} />}
                  checkedIcon={
                    <MicOff sx={{ color: RED_COLOR, fontSize: 18 }} />
                  }
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, fontSize: "0.65rem" }}
                >
                  MUTE MIC
                </Typography>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={speakerMuted}
                  onChange={(e) => {
                    const checked = e.target.checked;

                    setSpeakerMuted(checked);
                    persistAudioSettings({ speakerMuted: checked });
                  }}
                  icon={
                    <VolumeUp sx={{ fontSize: 18, color: ORANGE_MAIN }} />
                  }
                  checkedIcon={
                    <VolumeOff sx={{ color: RED_COLOR, fontSize: 18 }} />
                  }
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, fontSize: "0.65rem" }}
                >
                  MUTE VOICE
                </Typography>
              }
            />
          </Box>

          <Box
            sx={{
              height: 70,
              bgcolor: contactCenterTheme.chat.inputBg,
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.8,
              mb: 2,
            }}
          >
            {(micLevel > 0 || isTestingMic || isTestingSpeaker
              ? [...Array(10)]
              : [4, 7, 5, 9, 6, 10, 8, 5, 7, 4]
            ).map((heightValue, index) => {
              const active = micLevel > 0;

              const height = active
                ? Math.max(
                  6,
                  (micLevel / 100) * 55 * (Math.random() * 0.5 + 0.8),
                )
                : Number(heightValue) * 2.5;

              return (
                <Box
                  key={index}
                  sx={{
                    width: 4,
                    bgcolor: active ? ORANGE_MAIN : ORANGE_LIGHT,
                    borderRadius: "2px",
                    height,
                    transition: "height 0.05s ease",
                  }}
                />
              );
            })}
          </Box>

          <Box display="flex" gap={1.5} mb={2.5}>
            <Button
              fullWidth
              size="small"
              disableRipple
              variant={isTestingMic ? "contained" : "outlined"}
              onClick={startMicTest}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 800,
                fontSize: "0.75rem",
                bgcolor: isTestingMic
                  ? `${ORANGE_MAIN} !important`
                  : "transparent",
                color: isTestingMic ? "#fff" : ORANGE_MAIN,
                borderColor: ORANGE_MAIN,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: isTestingMic
                    ? ORANGE_MAIN
                    : alpha(ORANGE_MAIN, 0.08),
                  borderColor: ORANGE_MAIN,
                  boxShadow: "none",
                },
                "&.MuiButton-root": {
                  WebkitTapHighlightColor: "transparent",
                },
              }}
            >
              {isTestingMic ? "Stop Mic" : "Test Mic"}
            </Button>

            <Button
              fullWidth
              size="small"
              disableRipple
              variant={isTestingSpeaker ? "contained" : "outlined"}
              onClick={playTestSound}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 800,
                fontSize: "0.75rem",
                bgcolor: isTestingSpeaker
                  ? `${ORANGE_MAIN} !important`
                  : "transparent",
                color: isTestingSpeaker ? "#fff" : ORANGE_MAIN,
                borderColor: ORANGE_MAIN,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: isTestingSpeaker
                    ? ORANGE_MAIN
                    : alpha(ORANGE_MAIN, 0.08),
                  borderColor: ORANGE_MAIN,
                  boxShadow: "none",
                },
                "&.MuiButton-root": {
                  WebkitTapHighlightColor: "transparent",
                },
              }}
            >
              {isTestingSpeaker ? "Stop Spk" : "Test Spk"}
            </Button>
          </Box>

          <Stack spacing={2}>
            <Box>
              <Box display="flex" justifyContent="space-between">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {inputSensitivity === 0 ? (
                    <MicOff sx={{ fontSize: 18, color: "text.secondary" }} />
                  ) : (
                    <Mic sx={{ fontSize: 18, color: "text.secondary" }} />
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      ...styles.label,
                      color: "text.secondary",
                      display: "block",
                      mb: 0.2,
                      fontSize: "0.65rem",
                    }}
                  >
                    INPUT SENSITIVITY
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    ...styles.label,
                    color: "text.secondary",
                    display: "block",
                    mb: 0.2,
                    fontSize: "0.65rem",
                  }}
                >
                  {inputSensitivity}%
                </Typography>
              </Box>

              <Slider
                disabled={micMuted || isTestingSpeaker}
                size="small"
                value={inputSensitivity}
                onChange={(_, value) => {
                  setInputSensitivity(value as number);
                }}
                onChangeCommitted={(_, value) => {
                  persistAudioSettings({
                    inputSensitivity: value as number,
                  });
                }}
                sx={styles.slider}
              />
            </Box>

            <Box>
              <Box display="flex" justifyContent="space-between">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {outputVolume === 0 ? (
                    <VolumeOff
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  ) : (
                    <VolumeUp
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      ...styles.label,
                      color: "text.secondary",
                      display: "block",
                      mb: 0.2,
                      fontSize: "0.65rem",
                    }}
                  >
                    OUTPUT VOLUME
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    ...styles.label,
                    color: "text.secondary",
                    display: "block",
                    mb: 0.2,
                    fontSize: "0.65rem",
                  }}
                >
                  {outputVolume}%
                </Typography>
              </Box>

              <Slider
                disabled={speakerMuted || isTestingMic}
                size="small"
                value={outputVolume}
                onChange={(_, value) => setOutputVolume(value as number)}
                onChangeCommitted={(_, value) => {
                  persistAudioSettings({
                    outputVolume: value as number,
                  });
                }}
                sx={styles.slider}
              />
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
